import React from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { Award, ShieldCheck, Compass, Star, Sparkles, Activity, Layers, BarChart2 } from 'lucide-react';
import { getPlanetFullName, getPlanetShortCode, getZodiacName, getAvasthaName, getAvasthaEffect } from '../utils/language';

interface ShadbalaViewProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const ShadbalaView: React.FC<ShadbalaViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  const { shadbala, charaKarakas, avasthas } = kundali;

  if (!shadbala) {
    return <div className="p-6 text-center text-astro-muted">{isHi ? 'षड्बल गणना उपलब्ध नहीं है।' : 'Shadbala metrics not calculated.'}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-astro-primary/10 border border-astro-primary/20 text-astro-primary font-bold flex items-center justify-center text-xl shadow-sm font-serif">
            <Award className="w-6 h-6 text-astro-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-astro-text font-serif">
                {isHi ? 'पाराशरी षड्बल एवं भाव बल' : 'Parashari Shadbala & Bhava Bala'}
              </h2>
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2 py-0.5 rounded-md">
                {isHi ? 'बृहत पाराशर मानक' : 'Parashara Lite Standard'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {isHi ? 'ग्रहों का 6 प्रकार का बल (षड्बल) एवं 12 भावों का सामर्थ्य विश्लेषण' : '6-fold strength analysis of Planets (Grahas) & 12 Houses (Bhavas)'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <div className="px-3 py-1.5 rounded-xl bg-astro-card border border-astro-primary/30">
            <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'सर्वाधिक बलवान ग्रह' : 'Strongest Graha'}</span>
            <span className="font-bold text-astro-primary">{getPlanetFullName(shadbala.strongestPlanet as any, lang)}</span>
          </div>
          <div className="px-3 py-1.5 rounded-xl bg-astro-card border border-rose-300">
            <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'न्यूनतम बलवान ग्रह' : 'Weakest Graha'}</span>
            <span className="font-bold text-rose-600">{getPlanetFullName(shadbala.weakestPlanet as any, lang)}</span>
          </div>
        </div>
      </div>

      {/* 1. Planetary Shadbala Bar Graphs & Visual Breakdown */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">
              {isHi ? 'षड्बल ग्रहीय सामर्थ्य ग्राफ' : 'Shatbal Planetary Strength Analytics Graph'}
            </h3>
          </div>
          <span className="text-xs text-astro-secondary-text">{isHi ? 'प्राप्त रूप / आवश्यक रूप अनुपात' : 'Actual vs Required Rupas Ratio'}</span>
        </div>

        {/* Visual Bar Graph */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shadbala.planets.map((p) => {
            const pct = Math.min(Math.round(p.ratio * 100), 200); // 100% = required
            let barColor = 'bg-[#6F9690]';
            let textColor = 'text-astro-primary';
            if (p.ratio >= 1.25) {
              barColor = 'bg-astro-primary';
              textColor = 'text-astro-primary';
            } else if (p.ratio < 1.0) {
              barColor = 'bg-rose-500';
              textColor = 'text-rose-600';
            }

            return (
              <div key={p.planet} className="bg-astro-card p-3.5 rounded-xl border border-astro-border space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-astro-text font-serif">{getPlanetFullName(p.planet as any, lang)}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${textColor} bg-astro-bg`}>
                      {isHi ? (p.strengthCategory === 'Very Strong' ? 'अति बलवान' : p.strengthCategory === 'Strong' ? 'बलवान' : 'मध्यम/कम') : p.strengthCategory}
                    </span>
                  </div>
                  <div className="text-right font-mono">
                    <span className="font-bold text-astro-primary">{p.totalRupas} R</span>
                    <span className="text-astro-secondary-text text-[10px] ml-1">/ {p.requiredRupas} R req</span>
                  </div>
                </div>

                {/* Progress Bar Container */}
                <div className="relative w-full h-3 bg-astro-border rounded-full overflow-hidden border border-astro-border">
                  <div
                    className={`h-full ${barColor} transition-all duration-500 rounded-full`}
                    style={{ width: `${Math.min(pct / 2, 100)}%` }}
                  />
                  {/* 100% Required Marker Line */}
                  <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-astro-primary z-10 opacity-70" title="Required Benchmark" />
                </div>

                {/* Sub Balas Mini Chips */}
                <div className="flex flex-wrap gap-1 text-[9px] font-mono text-astro-secondary-text">
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'स्थान' : 'Sthana'}: {p.sthanaBala}</span>
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'दिग्' : 'Dig'}: {p.digBala}</span>
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'काल' : 'Kaala'}: {p.kaalaBala}</span>
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'चेष्टा' : 'Cheshta'}: {p.cheshtaBala}</span>
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'नैसर्गिक' : 'Naisargika'}: {p.naisargikaBala}</span>
                  <span className="bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border">{isHi ? 'दृग्' : 'Drik'}: {p.drikBala}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Planetary Shadbala Table */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">{isHi ? 'षड्बल विस्तृत तालिका' : 'Planetary Shadbala Breakdown Table'}</h3>
          </div>
          <span className="text-xs text-astro-secondary-text">1 {isHi ? 'रूप' : 'Rupa'} = 60 {isHi ? 'विरूप' : 'Virupas'}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] tracking-wider font-semibold bg-astro-card">
                <th className="py-3 px-3">{isHi ? 'ग्रह' : 'Planet'}</th>
                <th className="py-3 px-3">{isHi ? 'स्थान' : 'Sthana'}</th>
                <th className="py-3 px-3">{isHi ? 'दिग्' : 'Dig'}</th>
                <th className="py-3 px-3">{isHi ? 'काल' : 'Kaala'}</th>
                <th className="py-3 px-3">{isHi ? 'चेष्टा' : 'Cheshta'}</th>
                <th className="py-3 px-3">{isHi ? 'नैसर्गिक' : 'Naisargika'}</th>
                <th className="py-3 px-3">{isHi ? 'दृग्' : 'Drik'}</th>
                <th className="py-3 px-3">{isHi ? 'कुल (रूप)' : 'Total (Rupas)'}</th>
                <th className="py-3 px-3">{isHi ? 'अपेक्षित' : 'Req (Rupas)'}</th>
                <th className="py-3 px-3">{isHi ? 'अनुपात' : 'Ratio'}</th>
                <th className="py-3 px-3">{isHi ? 'श्रेणी' : 'Category'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border text-astro-text font-medium">
              {shadbala.planets.map((p) => {
                let badgeStyle = 'bg-astro-card text-astro-text border border-astro-border';
                if (p.strengthCategory === 'Very Strong') badgeStyle = 'bg-astro-primary/15 text-astro-primary border border-astro-primary/30';
                else if (p.strengthCategory === 'Strong') badgeStyle = 'bg-[#6F9690]/20 text-astro-primary border border-[#6F9690]/40';
                else if (p.strengthCategory === 'Weak') badgeStyle = 'bg-rose-100 text-rose-700 border border-rose-300';

                return (
                  <tr key={p.planet} className="hover:bg-astro-card transition-colors">
                    <td className="py-3 px-3 font-bold text-astro-primary flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-astro-primary" />
                      {getPlanetFullName(p.planet as any, lang)}
                    </td>
                    <td className="py-3 px-3 font-mono">{p.sthanaBala}</td>
                    <td className="py-3 px-3 font-mono">{p.digBala}</td>
                    <td className="py-3 px-3 font-mono">{p.kaalaBala}</td>
                    <td className="py-3 px-3 font-mono">{p.cheshtaBala}</td>
                    <td className="py-3 px-3 font-mono">{p.naisargikaBala}</td>
                    <td className="py-3 px-3 font-mono">{p.drikBala}</td>
                    <td className="py-3 px-3 font-mono font-bold text-astro-text">{p.totalRupas} R</td>
                    <td className="py-3 px-3 font-mono text-astro-secondary-text">{p.requiredRupas} R</td>
                    <td className="py-3 px-3 font-mono font-bold text-astro-primary">{p.ratio}</td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${badgeStyle}`}>
                        {isHi ? (p.strengthCategory === 'Very Strong' ? 'अति बलवान' : p.strengthCategory === 'Strong' ? 'बलवान' : 'मध्यम/कम') : p.strengthCategory}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2. Jaimini Chara Karakas & Planetary Avasthas Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jaimini Chara Karakas */}
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
            <Sparkles className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">{isHi ? 'जैमिनी 7 चर कारक' : 'Jaimini 7 Chara Karakas'}</h3>
          </div>

          <div className="space-y-2.5">
            {charaKarakas?.map((k) => (
              <div
                key={k.code}
                className="p-3 bg-astro-card rounded-xl border border-astro-border flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2.5">
                  <span className="px-2 py-1 rounded-md bg-astro-primary/10 text-astro-primary border border-astro-primary/20 font-mono font-bold text-[10px]">
                    {k.code}
                  </span>
                  <div>
                    <span className="font-bold text-astro-text block">{k.name} ({getPlanetFullName(k.planet as any, lang)})</span>
                    <span className="text-[10px] text-astro-secondary-text block">{k.significatorFor}</span>
                  </div>
                </div>
                <span className="font-mono text-astro-primary font-semibold">{k.formattedDegree}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Planetary Avasthas */}
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
            <Activity className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">{isHi ? 'ग्रहीय बालादि अवस्थाएं' : 'Planetary Age Avasthas (Baladi)'}</h3>
          </div>

          <div className="space-y-2.5">
            {avasthas?.map((a) => (
              <div
                key={a.planet}
                className="p-3 bg-astro-card rounded-xl border border-astro-border flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-md bg-astro-primary/10 border border-astro-primary/20 text-astro-primary font-bold flex items-center justify-center text-[10px]">
                    {getPlanetShortCode(a.planet, lang)}
                  </span>
                  <div>
                    <span className="font-bold text-astro-text">{getPlanetFullName(a.planet as any, lang)}</span>
                    <span className="text-[10px] text-astro-secondary-text block">
                      {getAvasthaEffect(a.avastha, lang, a.percentage)}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <span className="font-semibold text-astro-primary block">{getAvasthaName(a.avastha, lang)}</span>
                  <span className="text-[10px] text-astro-secondary-text font-mono">{a.percentage}% {isHi ? 'सामर्थ्य' : 'Power'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. 12 House Bhava Bala Grid */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
          <Layers className="w-5 h-5 text-astro-primary" />
          <h3 className="font-bold text-astro-primary font-serif text-base">{isHi ? '12 भाव बल एवं सामर्थ्य' : '12 Bhava (House) Strengths'}</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shadbala.bhavaBala.map((bh) => (
            <div
              key={bh.houseNumber}
              className="p-3 bg-astro-card rounded-xl border border-astro-border flex items-center justify-between text-xs"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-astro-primary/10 text-astro-primary font-bold flex items-center justify-center text-[11px]">
                    {isHi ? `भाव ${bh.houseNumber}` : `H${bh.houseNumber}`}
                  </span>
                  <span className="font-bold text-astro-text">{getZodiacName(bh.sign, lang)}</span>
                </div>
                <span className="text-[10px] text-astro-secondary-text block mt-1">{isHi ? 'स्वामी' : 'Lord'}: {getPlanetFullName(bh.signLord as any, lang)}</span>
              </div>

              <div className="text-right">
                <span className="font-mono font-bold text-astro-primary block text-sm">{bh.totalRupas} R</span>
                <span
                  className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                    bh.category === 'Strong' ? 'bg-astro-primary/15 text-astro-primary' : 'bg-astro-border/60 text-astro-secondary-text'
                  }`}
                >
                  {isHi ? (bh.category === 'Strong' ? 'बलवान' : 'सामान्य') : bh.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
